'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import { getDataVariableKey, getThemeSettingsKeys } from '../../utils/themeSettings';
import { getCSSVariablesMap } from '../../utils/cssVars';
import { updateAssetUrlTimestamp } from '../../utils/urls';
import { isPending, isSucceeded } from '../../utils/requestStatus';
import shallowEqual from '../../utils/shallowEqual';
import { CustomPropTypes, INLINED_STYLES_ATTRIBUTE, TEMPLATE_REFRESH_DOM_TAG_NAME } from '../../constants';
import * as PostMessageTypes from '../../constants/PostMessageTypes';
import http from 'hub-http/clients/noAuthApiClient';
import { jsx as _jsx } from "react/jsx-runtime";
const withThemesSettingsMap = () => WrappedComponent => {
  var _class;
  return _class = class extends Component {
    render() {
      const {
        clearUpdatedThemeSettings,
        hasBufferedChanges,
        hasHotReloadingAccess,
        messenger,
        onError,
        pageLevelOverride,
        previewLoaded,
        reloadIframePreview,
        saveRequestStatus,
        selectedBreakpoint,
        theme,
        themeSettings,
        updatedSettings
      } = this.props;
      const settingsVariablesList = getThemeSettingsKeys(theme);
      const CSSVariablesMap = !hasHotReloadingAccess ? getCSSVariablesMap(theme, themeSettings) : {};
      return /*#__PURE__*/_jsx(WrappedComponent, {
        clearUpdatedThemeSettings: clearUpdatedThemeSettings,
        CSSVariablesMap: CSSVariablesMap,
        hasBufferedChanges: hasBufferedChanges,
        hasHotReloadingAccess: hasHotReloadingAccess,
        messenger: messenger,
        onError: onError,
        pageLevelOverride: pageLevelOverride,
        previewLoaded: previewLoaded,
        reloadIframePreview: reloadIframePreview,
        saveRequestStatus: saveRequestStatus,
        selectedBreakpoint: selectedBreakpoint,
        settingsVariablesList: settingsVariablesList,
        updatedSettings: updatedSettings
      });
    }
  }, _class.propTypes = {
    clearUpdatedThemeSettings: PropTypes.func,
    hasBufferedChanges: PropTypes.bool.isRequired,
    hasHotReloadingAccess: PropTypes.bool.isRequired,
    messenger: PropTypes.object,
    onError: PropTypes.func,
    pageLevelOverride: PropTypes.bool.isRequired,
    previewLoaded: PropTypes.bool.isRequired,
    reloadIframePreview: PropTypes.func,
    saveRequestStatus: CustomPropTypes.requestStatus,
    selectedBreakpoint: PropTypes.string,
    theme: PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.object)
    }).isRequired,
    themeSettings: PropTypes.object.isRequired,
    updatedSettings: PropTypes.arrayOf(PropTypes.array)
  }, _class.defaultProps = {
    hasBufferedChanges: false,
    hasHotReloadingAccess: true,
    pageLevelOverride: false,
    selectedBreakpoint: '',
    theme: {},
    themeSettings: {},
    updatedSettings: []
  }, _class;
};
class ThemeSettingsPreview extends Component {
  constructor(props) {
    super(props);
    this.reloadPreview = () => {
      const {
        messenger,
        pageLevelOverride,
        reloadIframePreview
      } = this.props;
      if (pageLevelOverride) {
        reloadIframePreview();
      } else {
        messenger.postMessage({
          type: PostMessageTypes.IFRAME_REDUX_ACTION,
          reduxAction: reloadIframePreview()
        });
      }
    };
    this.findAssetsToUpdate = updatedSettingsKeys => {
      const assetsToUpdate = [];
      for (let i = 0; i < updatedSettingsKeys.length; i++) {
        const setting = updatedSettingsKeys[i];
        const themeSettingsAssets = document.querySelectorAll(`[${setting}]`);
        for (let j = 0; j < themeSettingsAssets.length; j++) {
          const asset = themeSettingsAssets[j];

          // If the template needs to reload, refresh the preview
          // and don't hot reload any assets
          if (asset.tagName === TEMPLATE_REFRESH_DOM_TAG_NAME) {
            this.reloadPreview();
            return [];
          } else if (!assetsToUpdate.includes(asset)) {
            assetsToUpdate.push(asset);
          }
        }
      }
      return assetsToUpdate;
    };
    this.updateAssets = (settingsVariablesList, updatedSettings) => {
      const {
        onError
      } = this.props;

      // For situations where no updated settings key exists, update all assets
      const updatedSettingsKeys = updatedSettings.length ? updatedSettings.map(setting => getDataVariableKey(setting)) : settingsVariablesList;
      const linkAssets = this.findAssetsToUpdate(updatedSettingsKeys);
      linkAssets.forEach(asset => {
        // Inlined assets have a data-hs-source attribute and need to have
        // the stylesheet contents fetched
        const dataSource = asset.getAttribute(INLINED_STYLES_ATTRIBUTE);
        if (dataSource) {
          http.get(dataSource).then(result => asset.innerHTML = result).catch(err => onError(err));
        } else {
          const assetLink = asset.href;
          asset.href = updateAssetUrlTimestamp(assetLink);
        }
      });
    };
    this.setCSSVariables = settingsVariablesMap => {
      let {
        styleEl
      } = this;
      if (!styleEl) {
        styleEl = this.styleEl = document.createElement('style');
        styleEl.className = '_HS-theme-settings-styles';
        document.getElementsByTagName('body')[0].appendChild(styleEl);
      }
      const styleSheet = styleEl.sheet;
      const stylesText = Object.keys(settingsVariablesMap).reduce((final, key) => `${final} ${key}: ${settingsVariablesMap[key]};`, '');
      if (styleSheet.cssRules[0]) {
        styleSheet.deleteRule(0);
      }
      styleSheet.insertRule(`:root { ${stylesText} }`, 0);
    };
    const {
      CSSVariablesMap,
      hasHotReloadingAccess
    } = props;
    if (!hasHotReloadingAccess) {
      this.setCSSVariables(CSSVariablesMap);
    }
  }
  shouldComponentUpdate(nextProps) {
    const {
      clearUpdatedThemeSettings,
      CSSVariablesMap,
      hasBufferedChanges,
      hasHotReloadingAccess,
      pageLevelOverride,
      previewLoaded,
      messenger,
      saveRequestStatus
    } = this.props;
    const {
      CSSVariablesMap: nextCSSVariablesMap,
      hasBufferedChanges: nextHasBufferedChanges,
      previewLoaded: nextPreviewLoaded,
      saveRequestStatus: nextSaveRequestStatus,
      selectedBreakpoint,
      settingsVariablesList: nextSettingsVariablesList,
      updatedSettings
    } = nextProps;
    if (hasHotReloadingAccess) {
      if (isPending(saveRequestStatus) && isSucceeded(nextSaveRequestStatus) || hasBufferedChanges && !nextHasBufferedChanges ||
      // If changes are made before the preview loads trigger hot reloading so
      // the new settings are applied right after load
      !previewLoaded && nextPreviewLoaded && updatedSettings.length) {
        // If a breakpoint is selected, reload the preview.
        // Remove this once hot reloading works with breakpoint editing.
        if (selectedBreakpoint) {
          this.reloadPreview();
        } else {
          this.updateAssets(nextSettingsVariablesList, updatedSettings);
        }

        // TODO: set this up like the global content editor to
        // allow for dispatching actions normally and remove else if
        if (messenger && nextPreviewLoaded) {
          messenger.postMessage({
            type: PostMessageTypes.IFRAME_REDUX_ACTION,
            reduxAction: clearUpdatedThemeSettings()
          });
        } else if (pageLevelOverride) {
          clearUpdatedThemeSettings();
        }
      }
    } else if (!shallowEqual(nextCSSVariablesMap, CSSVariablesMap)) {
      this.setCSSVariables(nextCSSVariablesMap);
    }
    return false;
  }
  componentWillUnmount() {
    if (this.styleEl) {
      this.styleEl.remove();
    }
  }

  // TODO Remove when hot reloading ungated to all

  render() {
    return null;
  }
}
ThemeSettingsPreview.propTypes = {
  clearUpdatedThemeSettings: PropTypes.func,
  CSSVariablesMap: PropTypes.object.isRequired,
  hasBufferedChanges: PropTypes.bool.isRequired,
  hasHotReloadingAccess: PropTypes.bool.isRequired,
  messenger: PropTypes.object,
  onError: PropTypes.func,
  pageLevelOverride: PropTypes.bool.isRequired,
  previewLoaded: PropTypes.bool.isRequired,
  reloadIframePreview: PropTypes.func,
  saveRequestStatus: CustomPropTypes.requestStatus,
  selectedBreakpoint: PropTypes.string,
  settingsVariablesList: PropTypes.arrayOf(PropTypes.string),
  updatedSettings: PropTypes.arrayOf(PropTypes.array)
};
ThemeSettingsPreview.defaultProps = {
  CSSVariablesMap: {},
  hasBufferedChanges: false,
  hasHotReloadingAccess: true,
  pageLevelOverride: false,
  settingsVariablesList: [],
  updatedSettings: []
};
export default withThemesSettingsMap()(ThemeSettingsPreview);